import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { AppState, AppStateStatus } from "react-native";
import updateService, { UpdateInfo } from "../services/updateService";

type UpdateInfoState = UpdateInfo;

export const useUpdates = () => {
  const [updateInfo, setUpdateInfo] = useState<UpdateInfoState>({
    isAvailable: false,
    isUpdatePending: false,
    isUpdateLoading: false,
  });

  const [isChecking, setIsChecking] = useState(false);
  const [isActive, setIsActive] = useState(AppState.currentState === "active");

  // Prevent concurrent checks
  const isCheckingRef = useRef(false);
  const inflightRef = useRef<Promise<UpdateInfo | null> | null>(null);

  const checkForUpdates = useCallback(async () => {
    if (isCheckingRef.current || inflightRef.current) {
      // If a check is in-flight, just return it (no-op for callers that don't await)
      return inflightRef.current;
    }
    isCheckingRef.current = true;
    setIsChecking(true);

    const p = updateService
      .checkForUpdates()
      .then((info) => {
        setUpdateInfo((prev) => ({ ...prev, ...info, error: "" }));

        if ((info as any).isDevelopment) {
          // Keep this in dev only; guard with ENV in your service if needed
          console.log("Development mode detected - updates are simulated");
        }

        return info;
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : String(error);
        console.error("Error checking for updates:", message);
        setUpdateInfo((prev) => ({
          ...prev,
          error: `Check failed: ${message}`,
        }));
        return null;
      })
      .finally(() => {
        isCheckingRef.current = false;
        inflightRef.current = null;
        setIsChecking(false);
      });

    inflightRef.current = p;
    return p;
  }, []);

  const applyUpdate = useCallback(async () => {
    if (!updateInfo.isAvailable || updateInfo.isUpdateLoading) return false;

    // Set loading first so the UI reflects it even if apply triggers an immediate reload
    setUpdateInfo((prev) => ({ ...prev, isUpdateLoading: true }));

    try {
      const success = await updateService.applyUpdate();
      if (!success) {
        // Roll back loading flag if nothing actually happened
        setUpdateInfo((prev) => ({ ...prev, isUpdateLoading: false }));
      }
      return success;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.error("Error applying update:", message);
      setUpdateInfo((prev) => ({
        ...prev,
        isUpdateLoading: false,
        error: `Apply failed: ${message}`,
      }));
      return false;
    }
  }, [updateInfo.isAvailable, updateInfo.isUpdateLoading]);

  // Keep an explicit active flag
  useEffect(() => {
    const handleAppStateChange = (next: AppStateStatus) => {
      const active = next === "active";
      setIsActive(active);
      if (active) {
        checkForUpdates();
      }
    };

    const sub = AppState.addEventListener("change", handleAppStateChange);

    // Initial check
    checkForUpdates();

    return () => sub.remove();
  }, [checkForUpdates]);

  // Auto-check for updates every 30 minutes while active
  useEffect(() => {
    const interval = setInterval(
      () => {
        if (isActive) {
          void checkForUpdates();
        }
      },
      30 * 60 * 1000,
    );

    return () => clearInterval(interval);
  }, [isActive, checkForUpdates]);

  // These values are effectively static per app run; memoize to avoid needless re-renders
  const staticInfo = useMemo(
    () => ({
      isUpdatesEnabled: updateService.isUpdatesEnabled(),
      channel: updateService.getChannel(),
      runtimeVersion: updateService.getRuntimeVersion(),
      updateId: updateService.getUpdateId(),
      isRunningFromUpdate: updateService.isRunningFromUpdate(),
      buildType: updateService.getBuildType(),
    }),
    [],
  );

  return {
    updateInfo,
    isChecking,
    checkForUpdates,
    applyUpdate,
    ...staticInfo,
  };
};
